import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { isApiError, userErrorMessage } from '../api/errors.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Field } from '../components/Field.js';
import { FormMessage } from '../components/FormMessage.js';
import { SubmitButton } from '../components/SubmitButton.js';
import { useAuth } from '../auth/context.js';
import type { LoginValues } from '../auth/schemas.js';
import { useAuthSchemas } from '../auth/useAuthSchemas.js';
import { useI18n } from '../i18n/context.js';

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const { login: loginSchema } = useAuthSchemas();
  const { t } = useI18n();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      // Where to go next is GuestOnly's: it redirects as soon as the user is set.
      await login(values.email, values.password);
    } catch (error: unknown) {
      setFormError(
        isApiError(error) && error.status === 401
          ? t.auth.login.failed
          : userErrorMessage(error, t),
      );
    }
  });

  return (
    <AuthLayout
      title={t.auth.login.title}
      footer={
        <>
          {t.auth.noAccount} <Link to="/register">{t.auth.signUp}</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError ? <FormMessage kind="error">{formError}</FormMessage> : null}

        <Field
          label={t.common.email}
          type="email"
          autoComplete="email"
          placeholder={t.common.emailPlaceholder}
          error={errors.email?.message}
          {...register('email')}
        />

        <Field
          label={t.common.password}
          type="password"
          autoComplete="current-password"
          error={errors.password?.message}
          {...register('password')}
        />

        <SubmitButton label={t.auth.login.submit} pending={isSubmitting} />

        <Link
          to="/forgot-password"
          className="text-center text-sm text-[var(--kitup-color-accent)] underline underline-offset-2"
        >
          {t.auth.forgotLink}
        </Link>
      </form>
    </AuthLayout>
  );
}
